import React from "react";

import { faArrowUpRightFromSquare, faCircleCheck, faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Chip, Text } from "@performyard/py-components";

import type { FormSuggestionState } from "../types";

import { QUESTION_TYPE_LABEL } from "../utils/question-type-labels";

type FormPreviewCardProps = {
	suggestionState: FormSuggestionState;
	onCreateForm: () => void;
	isCreating?: boolean;
};

export const FormPreviewCard: React.FC<FormPreviewCardProps> = ({ suggestionState, onCreateForm, isCreating }) => {
	const { suggestion, status, createdFormBaseId } = suggestionState;

	if (status === "deleted") {
		return null;
	}

	const isCreated = status === "created";
	const questionCount = suggestion.questions.length;

	return (
		<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-0 px-3 py-4">
			{/* Name and question count sit flush — no gap between them per node 167:35130. */}
			<div className="flex flex-col">
				<div className="flex max-w-full min-w-0 items-start gap-1">
					<Text
						size="paragraph-3"
						weight="semi-bold"
						color="neutral-9"
						dangerouslySetClassName="min-w-0 flex-1 break-words"
					>
						{suggestion.name}
					</Text>
					{isCreated && (
						<span className="shrink-0">
							<Chip color="green" size={20} icon={faCircleCheck} label="Form Created" />
						</span>
					)}
				</div>
				<Text size="caption" color="neutral-9">
					{questionCount} {questionCount === 1 ? "question" : "questions"}
				</Text>
			</div>

			<ol className="m-0 flex list-none flex-col gap-1 p-0">
				{suggestion.questions.map((q, idx) => (
					<li key={`${suggestion.id}-q-${idx}`} className="grid grid-cols-[1rem_1fr] gap-x-2">
						<Text size="caption" weight="semi-bold" color="neutral-9" dangerouslySetClassName="text-right">
							{idx + 1}.
						</Text>
						<div className="flex flex-col gap-0.5">
							<Text size="caption" weight="semi-bold" color="neutral-9">
								{q.question}
								{q.is_required ? <span className="text-red-7"> *</span> : null}
							</Text>
							<Text size="extra-small" color="neutral-7">
								{QUESTION_TYPE_LABEL[q.type]}
							</Text>
						</div>
					</li>
				))}
			</ol>

			{isCreated ? (
				<ButtonStandard
					variant="outline"
					color="neutral"
					size="small"
					iconRight={faArrowUpRightFromSquare}
					onClick={() => {
						if (createdFormBaseId) {
							window.open(`/edit-review-form/${createdFormBaseId}`, "_blank");
						}
					}}
				>
					View Form
				</ButtonStandard>
			) : (
				// Form creation isn't tethered to any page, so a restored card stays
				// actionable exactly like a live one — unlike coaching improvements.
				<ButtonStandard
					variant="outline"
					color="primary"
					size="small"
					iconLeft={faPlus}
					onClick={onCreateForm}
					disabled={isCreating}
					data-testid="askpy-create-form-button"
				>
					{isCreating ? "Creating..." : "Create Form"}
				</ButtonStandard>
			)}
		</div>
	);
};

export default FormPreviewCard;
